/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */

import { VNode } from "preact";
import { html } from "htm/preact";
import { BlissSymbolInfoType, LayoutInfoType } from "../index.d";
import { adaptivePaletteGlobals } from "../state/GlobalData";
import { announceIfEnabled, speakUnavailable } from "../utils/SpeechUtils";
import { atRootWithNoOverlay, hideNavigationOverlay, navigationOverlayOpen } from "../core/NavigationOverlay";
import type { Language } from "../i18n/I18n";
import { BlissSymbol } from "../components/BlissSymbol";
import "./ActionCodeCell.scss";

type CommandGoBackCellPropsType = {
  id: string,
  options: BlissSymbolInfoType & LayoutInfoType
};

/*
 * The implementation of the function invoked by, for example, activating a
 * CommandGoBackCell button/cell.  An open layer over the palette is one level: Back closes
 * it and stays on the palette.  Otherwise this determines which palette to go back to by
 * consulting the navigation stack and adjusts the stack accordingly.  The component
 * watching the stack redraws.
 */
export function goBackImpl (): void {
  if (navigationOverlayOpen()) {
    hideNavigationOverlay();
    return;
  }
  const { navigationStack } = adaptivePaletteGlobals;

  // The stack holds the palette itself, so there is nothing to look up: looking it up by
  // `name` would miss a palette stored under a key that differs from its name.
  const paletteToGoBackTo = navigationStack.peek();
  if (paletteToGoBackTo) {
    navigationStack.popAndSetCurrent(paletteToGoBackTo);
  }
};

/*
 * Event handler for an CommandGoBackCellPropsType button/cell that, when
 * clicked, goes back one palette.
 */
const goBackToPalette = (event: Event, language?: Language): void => {
  const button = event.currentTarget as HTMLElement;
  if (atRootWithNoOverlay()) {
    speakUnavailable(button.innerText);
    return;
  }
  announceIfEnabled(button.innerText, language);
  goBackImpl();
};

export function CommandGoBackCell (props: CommandGoBackCellPropsType): VNode {

  const {
    columnStart, columnSpan, rowStart, rowSpan, composition, label, labelLanguage
  } = props.options;
  const ariaControlsId = adaptivePaletteGlobals.mainPaletteContainerId;

  // Marked unavailable rather than `disabled`: a disabled button leaves the tab order,
  // which costs a switch or eye-gaze user their scan position.  Unavailable when there is
  // nowhere to go back to.
  const unavailable = atRootWithNoOverlay();

  const gridStyles = `
    grid-column: ${columnStart} / span ${columnSpan};
    grid-row: ${rowStart} / span ${rowSpan};
  `;

  return html`
    <button
      id="${props.id}" class="btn-command" style="${gridStyles}"
      aria-controls="${ariaControlsId}" aria-disabled=${unavailable}
      onClick=${(event: Event) => goBackToPalette(event, labelLanguage)}>
      <${BlissSymbol} composition=${composition} label=${label} />
    </button>
  `;
}
